import { Epic, combineEpics, ofType } from 'redux-observable';
import { createTryActionsEpic, createTryActionFromStoreEpic, onTryGA } from './Common';
import {
    CREATE_BOOKING,
    CANCEL_BOOKING,
    ACCEPT_BOOKING_OFFERS,
    REJECT_BOOKING_OFFER,
    BookingAction,
    PAYMENT_DETAILS_ENTERED,
    LOAD_BOOKING,
    userBookingUpdated,
    SEND_USER_BOOKING_MESSAGE,
    NewBookingSchoolsChanged,
    NEW_BOOKING_SCHOOLS_CHANGED,
    NewBookingInstantSettingsChanged,
    NEW_BOOKING_INSTANT_SETTINGS_CHANGED,
    CREATE_INSTANT_BOOKING
} from '../actions/BookingActions';
import {
    StoreState,
    UserBooking
} from '../types';
import * as api from '../api';
import {
    CreateBookingRequest,
    createRequestId,
    CreateBooking,
    GetBooking
} from '../api/Commands';
import _ from 'lodash';
import { observeChanges } from '../firebase';
import { Action } from 'redux';
import { UserSignedIn, USER_SIGNED_IN } from '../actions/AuthActions';
import { map, switchMap, catchError } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { gaAddTransaction, gaAddItem, gaSendCart, gaClearCart } from '../analytics';

const createBooking = async (recaptchaResponse: string, { auth, booking }: StoreState) => {
    const user = auth.user;
    if ((!user || !user.uid) && (!booking.newBooking.email || !booking.newBooking.name)) {
        throw new Error('Tried to create a booking request without authenticating or providing a name and email');
    }

    if (!recaptchaResponse) {
        throw new Error('Tried to create a booking with no recaptchaResponse');
    }

    const { schools } = booking.newBooking;

    if (!schools || schools.length === 0) {
        throw new Error('Tried to create a booking with no schools');
    }

    const requests =
        _.flatMap(_.filter(booking.newBooking.requests, r => !!r.type), r =>
            _.flatMap(
                _.filter(schools, s => !s.bookingDisabled && s.activities.includes(r.activity || '')), s =>
                ({
                    ...r,
                    requestId: createRequestId(s.schoolId, r.activity || ''),
                    schoolId: s.schoolId,
                    group: r.group.filter(m => m.name && m.isSelected && m.skill)
                } as CreateBookingRequest))
        );

    if (requests.length === 0) {
        throw new Error('Tried to create a booking with no requests');
    }

    const command: CreateBooking = {
        name: booking.newBooking.name || '',
        email: booking.newBooking.email || '',
        tel: booking.newBooking.tel || '',
        recaptchaResponse: recaptchaResponse,
        requests
    };

    return await api.createBooking(command);
};

const loadBooking = async ({ userEmail, bookingId, bookingRef }: { userEmail: string, bookingId?: string, bookingRef?: string }, { auth }: StoreState) => {
    if (auth.user) {
        throw new Error('Tried to load booking when already authenticated');
    }

    const command: GetBooking = {
        bookingId: bookingId,
        bookingRef: bookingRef,
        userEmail: userEmail
    };

    return await api.getBooking(command);
};

const acceptOffers = async ({ bookingId }: { bookingId: string }, { auth, booking }: StoreState) => {
    const user = auth.user;
    if (!user || !user.uid) {
        throw new Error('Tried to accept offers without authenticating');
    }

    const b = booking.bookings[bookingId];
    if (!b) {
        throw new Error(`Booking ${bookingId} not found`);
    }

    const itinerary = booking.itineraries[bookingId];

    if (!itinerary) {
        throw new Error(`Booking itinerary ${bookingId} not found`);
    }

    if (!itinerary.items || itinerary.items.length === 0 || !itinerary.validation || !itinerary.validation.isValid) {
        throw new Error(`Invalid booking itinerary ${bookingId}`);
    }

    return await api.acceptOffers({
        bookingId,
        userId: user.uid,
        acceptedOffers: itinerary.items
    });
};

const rejectOffer = async ({ bookingId, schoolId, offerId, reason }: { bookingId: string, schoolId: string, offerId: string, reason: string }, { auth, booking }: StoreState) => {
    const user = auth.user;
    if (!user || !user.uid) {
        throw new Error('Tried to reject a booking offer without authenticating');
    }

    const b = booking.bookings[bookingId];
    if (!b) {
        throw new Error(`Booking ${bookingId} not found`);
    }

    const request = _.find(b.requests, r => r.schoolId === schoolId);
    if (!request) {
        throw new Error(`Request for booking ${bookingId} not found for school ${schoolId}`);
    }

    return await api.rejectOffer({
        bookingId,
        offerId,
        userId: user.uid,
        schoolId,
        requestId: request.requestId,
        reason
    });
};

const payForBooking = async ({ bookingId, schoolId, stripeToken }: { bookingId: string, schoolId: string, stripeToken: string }, { auth, booking }: StoreState) => {
    const user = auth.user;
    if (!user || !user.uid) {
        throw new Error('Tried to pay for a booking when not authenticated');
    }

    const b = booking.bookings[bookingId];
    if (!b) {
        throw new Error(`Booking ${bookingId} not found`);
    }

    try {
        gaEcommerce(b, schoolId);
    } catch (err) {
        console.error(err);
        gaClearCart();
    }

    const result = await api.payForBooking({
        bookingId,
        schoolId,
        stripeToken
    });

    gaSendCart();

    return result;
};

const createInstantBooking = async ({ stripeToken, name, email }: { stripeToken: string, name: string, email: string }, { auth, booking }: StoreState) => {
    const user = auth.user;
    if ((!user || !user.uid) && (!email || !name)) {
        throw new Error('Tried to create an instant booking without authenticating or providing a name and email');
    }

    const { instantOffers } = booking.newBooking;

    if (!instantOffers || instantOffers.length === 0) {
        throw new Error('Tried to create an instant booking with no instantOffers');
    }

    const result = await api.createInstantBooking({
        name: name || user?.displayName || '',
        email: email || user?.email || '',
        tel: user?.phoneNumber || '',
        stripeToken,
        instantOffers
    });

    return result;
};

const sendUserBookingMessage = async (sendBookingMessage: { bookingId: string, schoolId: string, message: string, messageId: string }, { auth }: StoreState) => {
    const user = auth.user;
    if (!user || !user.uid) {
        throw new Error('Tried to send a booking message when not authenticated');
    }

    await api.sendBookingMessage(sendBookingMessage);
};

const getInstantBookingSettings = async (action: NewBookingSchoolsChanged) => {
    const result = await api.getInstantBookingSettings({
        schoolIds: action.value.schools.map(s => s.schoolId)
    });

    return {
        type: NEW_BOOKING_INSTANT_SETTINGS_CHANGED,
        value: { 
            settings: result.settings,
            commissionModels: result.commissionModels 
        }
    } as NewBookingInstantSettingsChanged;
};

const gaEcommerce = (b: UserBooking, schoolId: string) => {
    // get all unpaid invoices for school
    const invoices = _.filter(b.invoices, i => i.schoolId === schoolId && !i.paid);

    if (invoices.length === 0) {
        return;
    }

    // calc total revenue for all school's unpaid invoices
    const subtotals = _.sumBy(invoices, i => i.subtotal);
    const fees = _.sumBy(invoices, i => i.fee);
    const ccy = invoices[0].currency; // assume all the same currency...

    gaAddTransaction({
        id: b.bookingId,
        affiliation: schoolId,
        revenue: subtotals + fees,
        currency: ccy
    });

    // get all offers for school
    const offers = _.flatMap(
        _.filter(b.requests, r => r.schoolId === schoolId), r => _.values(r.offers));

    _.forEach(invoices, i => {
        _.forEach(i.items, item => {
            // get offer corresponding to invoice item
            const o = offers.find(io => io.offerId === item.offerId);

            if (o && o.totalDays > 0 && o.hoursPerDay > 0) {
                const r = b.requests[o.requestId];

                if (!!r) {
                    // calc total number of hours and hourly rate of commission
                    const quantity = o.totalDays * o.hoursPerDay;
                    const unitPrice = (item.price) / quantity;

                    gaAddItem({
                        id: b.bookingId,
                        sku: `${r.activity}-${r.type}-${o.lessonFormat}`,
                        name: `${r.activity} ${r.type} ${o.lessonFormat}`,
                        category: r.activity || undefined,
                        price: unitPrice,
                        quantity: quantity,
                        currency: o.currency
                    });
                }
            }
        });
    });

    gaAddItem({
        id: b.bookingId,
        sku: `booking-fee`,
        name: `Fee`,
        category: 'Booking',
        price: fees / invoices.length,
        quantity: invoices.length,
        currency: ccy
    });
};

const cancelBooking = async (bookingId: string, { auth }: StoreState) => {
    const user = auth.user;
    if (!user || !user.uid) {
        throw new Error('Tried to cancel a booking request without authenticating');
    }

    return await api.cancelBooking({ bookingId });
};

const createBookingEpic: Epic<BookingAction, BookingAction, StoreState> =
    createTryActionsEpic(
        CREATE_BOOKING,
        (a, s) => createBooking(a.value.recaptchaResponse, s),
        (a, r) => r);

const createInstantBookingEpic: Epic<BookingAction, BookingAction, StoreState> =
    createTryActionsEpic(
        CREATE_INSTANT_BOOKING,
        (a, s) => createInstantBooking(a.value, s),
        (a, r) => r);

const loadBookingEpic: Epic<BookingAction, BookingAction, StoreState> =
    createTryActionsEpic(
        LOAD_BOOKING,
        (a, s) => loadBooking(a.value, s),
        (a, r) => r);

const loadBookingEpicFromStore: Epic<BookingAction, BookingAction, StoreState> =
    createTryActionFromStoreEpic(
        s => s.booking.getBookingRequest as BookingAction,
        (a, s) => loadBooking(a.value, s),
        (a, r) => r);

const cancelRequestEpic: Epic<BookingAction, BookingAction, StoreState> =
    createTryActionsEpic(
        CANCEL_BOOKING,
        (a, s) => cancelBooking(a.value, s),
        (a, r) => a.value);

const acceptOfferEpic: Epic<BookingAction, BookingAction, StoreState> =
    createTryActionsEpic(
        ACCEPT_BOOKING_OFFERS,
        (a, s) => acceptOffers(a.value, s),
        (a, r) => a.value.offerId);

const rejectOfferEpic: Epic<BookingAction, BookingAction, StoreState> =
    createTryActionsEpic(
        REJECT_BOOKING_OFFER,
        (a, s) => rejectOffer(a.value, s),
        (a, r) => a.value.offerId);

const paymentDetailsEnteredEpic: Epic<BookingAction, BookingAction, StoreState> =
    createTryActionsEpic(
        PAYMENT_DETAILS_ENTERED,
        (a, s) => payForBooking(a.value, s),
        (a, r) => a.value);

const sendUserBookingMessageEpic: Epic<BookingAction, BookingAction, StoreState> =
    createTryActionsEpic(
        SEND_USER_BOOKING_MESSAGE,
        (a, s) => sendUserBookingMessage(a.value, s),
        (a, r) => a.value);

const userBookingsUpdatedEpic: Epic<Action, Action, StoreState> =
    (action$, store$) =>
        action$
            .pipe(
                ofType<Action, UserSignedIn>(USER_SIGNED_IN),
                switchMap(a => observeChanges(`users/${a.value.user.uid}/bookings`)),
                map(userBookingUpdated),
                catchError((err, caught) => {
                    console.log('userBookingsUpdatedEpic', err);
                    return caught;
                })
            );

const getInstantBookingSettingsEpic: Epic<Action, Action, StoreState> =
    (action$, store$): Observable<NewBookingInstantSettingsChanged> =>
        action$
            .pipe(
                ofType<Action, NewBookingSchoolsChanged>(NEW_BOOKING_SCHOOLS_CHANGED),
                switchMap(a => onTryGA(a, () => getInstantBookingSettings(a))),
                catchError((err, caught) => {
                    console.log('getInstantBookingSettingsEpic', err);
                    return caught;
                })
            );

export default combineEpics(
    createBookingEpic,
    createInstantBookingEpic,
    loadBookingEpic,
    cancelRequestEpic,
    acceptOfferEpic,
    rejectOfferEpic,
    paymentDetailsEnteredEpic,
    userBookingsUpdatedEpic,
    loadBookingEpicFromStore,
    sendUserBookingMessageEpic,
    getInstantBookingSettingsEpic,
);
